import { AgentConversationKind } from '@this+that/models';
import type { StateCreator } from 'zustand';

import type {
  AttachHostArgs,
  DoBoxInputContext,
  FloatingAssistantStore,
  HostExecutorTarget,
  InputContext,
} from './types';
import { DRAWER_CUSTOM_WIDTH_STORAGE_KEY } from '../../components/assistant/FloatingAssistant/drawerConstants';
import type { CloudRunScope } from '../../utils/delegation/cloudRunOwnership';
import { useAgentConversationsStore } from '../AgentConversations';

function readStoredWidth(): number | null {
  try {
    const raw = localStorage.getItem(DRAWER_CUSTOM_WIDTH_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistWidth(px: number): void {
  try {
    localStorage.setItem(DRAWER_CUSTOM_WIDTH_STORAGE_KEY, String(px));
  } catch {
    return;
  }
}

// Focus left over from another entry must never paint in the new host, so it is kept only when provably this entry's.
function isFocusedConversationOwnedByEntry(
  context: DoBoxInputContext,
  focusedId: string,
): boolean {
  if (context.delegationConversationId === focusedId) {
    return true;
  }
  const slot = useAgentConversationsStore.getState().slots.get(focusedId);
  return slot?.conversation.sourceInboxEntryRef?.entryId === context.itemId;
}

export const createFloatingAssistantSlice: StateCreator<
  FloatingAssistantStore
> = (set, get) => ({
  isOpen: false,
  pendingPromptFromChip: null,
  pendingInputContext: null,
  customWidthPx: readStoredWidth(),
  hostSlot: null,
  hostContext: null,
  hostOnRequestClose: null,
  isHostReadOnly: false,
  isHostConversationHidden: false,
  openBeforeHosting: null,
  isHostAvailable: false,
  pendingHostedPanelOpenEntryId: null,
  hostedPanelFocusNonce: 0,
  hostExecutorTarget: null,
  isHostResolving: false,
  isHostExecutorUnavailable: false,
  focusedIdBeforeHosting: null,
  hostCloudRunScope: null,

  open: (initialPrompt?: string) => {
    set({
      isOpen: true,
      pendingPromptFromChip: initialPrompt ?? get().pendingPromptFromChip,
    });
  },

  openWithContext: (ctx: InputContext, initialPrompt?: string) => {
    set({
      isOpen: true,
      pendingInputContext: ctx,
      pendingPromptFromChip: initialPrompt ?? get().pendingPromptFromChip,
    });
  },

  close: () => {
    set({ isOpen: false });
  },

  consumePendingPrompt: () => {
    const pending = get().pendingPromptFromChip;
    set({ pendingPromptFromChip: null });
    return pending;
  },

  consumePendingInputContext: () => {
    const pending = get().pendingInputContext;
    set({ pendingInputContext: null });
    return pending;
  },

  setCustomWidthPx: (px: number) => {
    persistWidth(px);
    set({ customWidthPx: px });
  },

  attachHost: ({ slot, context, onRequestClose }: AttachHostArgs) => {
    const { focusedId, setFocusedId } = useAgentConversationsStore.getState();
    set({
      openBeforeHosting: get().isOpen,
      isOpen: true,
      hostSlot: slot,
      hostContext: context,
      hostOnRequestClose: onRequestClose,
      focusedIdBeforeHosting: focusedId,
    });
    if (
      context.kind === 'dobox' &&
      focusedId !== null &&
      !isFocusedConversationOwnedByEntry(context, focusedId)
    ) {
      setFocusedId(null);
    }
  },

  updateHostContext: (ctx: InputContext) => {
    if (get().hostSlot === null) {
      return;
    }
    set({ hostContext: ctx });
  },

  detachHost: () => {
    const { openBeforeHosting, focusedIdBeforeHosting } = get();
    const { focusedId, slots, setFocusedId } =
      useAgentConversationsStore.getState();
    const isFocusedOnExecutorConversation =
      focusedId !== null &&
      slots.get(focusedId)?.conversation.conversationKind ===
        AgentConversationKind.DOBOX_EXECUTOR;
    if (isFocusedOnExecutorConversation) {
      setFocusedId(focusedIdBeforeHosting);
    }
    set({
      isOpen: openBeforeHosting ?? false,
      hostSlot: null,
      hostContext: null,
      hostOnRequestClose: null,
      isHostReadOnly: false,
      isHostConversationHidden: false,
      openBeforeHosting: null,
      hostExecutorTarget: null,
      isHostResolving: false,
      isHostExecutorUnavailable: false,
      focusedIdBeforeHosting: null,
      hostCloudRunScope: null,
    });
  },

  setHostCloudRunScope: (scope: CloudRunScope | null) => {
    set({ hostCloudRunScope: scope });
  },

  setHostReadOnly: (isReadOnly: boolean) => {
    set({ isHostReadOnly: isReadOnly });
  },

  setHostConversationHidden: (isHidden: boolean) => {
    set({ isHostConversationHidden: isHidden });
  },

  setHostAvailable: (isAvailable: boolean) => {
    set({ isHostAvailable: isAvailable });
  },

  requestHostedPanelOpen: (entryId: string) => {
    set({ pendingHostedPanelOpenEntryId: entryId });
  },

  consumeHostedPanelOpenRequest: (entryId: string) => {
    if (get().pendingHostedPanelOpenEntryId !== entryId) {
      return false;
    }
    set({ pendingHostedPanelOpenEntryId: null });
    return true;
  },

  requestHostedPanelFocus: () => {
    set({ hostedPanelFocusNonce: get().hostedPanelFocusNonce + 1 });
  },

  setHostExecutorTarget: (target: HostExecutorTarget | null) => {
    set({ hostExecutorTarget: target });
  },

  setHostResolving: (isResolving: boolean) => {
    set({ isHostResolving: isResolving });
  },

  setHostExecutorUnavailable: (isUnavailable: boolean) => {
    set({ isHostExecutorUnavailable: isUnavailable });
  },
});
